import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
var _Object;
export var isTupleOfLength = function(array, length) {
    return array.length === length;
};
export var isTupleOfLengthGreaterThanOrEqual = function(array, length) {
    return array.length >= length;
};
export var isNotNull = function(input) {
    return input !== null;
};
export var isNotUndefined = function(input) {
    return input !== undefined;
};
/**
 * Checks if value is nullish (null or undefined). Equivalent to a `== null` check.
 *
 * @param {unknown} value the value to check
 * @returns {boolean} true if value is nullish (null or undefined), otherwise false
 */ export var isNullish = function(value) {
    return value === null || value === undefined;
};
export var keyBy = function(array, iteratee) {
    return (_Object = Object).assign.apply(_Object, [
        {}
    ].concat(_to_consumable_array(array.map(function(elem, index) {
        return _define_property({}, elem, iteratee(elem, index));
    }))));
};
/**
 * Strongly-typed Object.keys for a precise instantiated type (e.g. a IFoo.build from faire/web-api).
 *
 * This should never be used on anything that will be extended, as the type constraint won't be true,
 * since the keys would be a superset of the given type.
 */ export var protoKeys = function(proto) {
    return Object.keys(proto);
};
/**
 * Strongly-typed Object.values for a precise instantiated type (e.g. a IFoo.build from faire/web-api).
 *
 * This should never be used on anything that will be extended, as the type constraint won't be true,
 * since the keys/entries would be a superset of the given type.
 */ export var protoValues = function(proto) {
    return Object.values(proto);
};
/**
 * Strongly-typed Object.entries for a precise instantiated type (e.g. a IFoo.build from faire/web-api).
 *
 * This should never be used on anything that will be extended, as the type constraint won't be true,
 * since the keys/entries would be a superset of the given type.
 */ export var protoEntries = function(proto) {
    return Object.entries(proto);
};
export var enumValues = function(enumObject) {
    return Object.values(enumObject);
};
export var enumKeys = function(enumObject) {
    return Object.keys(enumObject);
};
export var isInEnum = function(value, values) {
    return values.includes(value);
};
export var isEnumValue = function(enumObject, value) {
    return enumValues(enumObject).includes(value);
};
export var isEnumKey = function(enumObject, value) {
    return enumKeys(enumObject).includes(value);
};
/**
 * Gets the enum value if the given key is in the enum, or return undefined.
 */ export var enumValue = function(enumObject, key) {
    if (isEnumKey(enumObject, key)) {
        return enumObject[key];
    }
    return undefined;
};
/**
 * Return the given value of `indexable` at the given key with the type
 * `unknown`.
 *
 * When the key isn't statically guaranteed to be part
 * of the object's schema, the return value could be anything, as object
 * values can have additional key/value pairs and still satisfy the type
 * signature of a smaller schema. For example, `{a: 1, b: 'hi'}` is a
 * valid instance of `{a: number}`, so indexing it with an arbitrary index
 * could return a string, even though string is never even mentioned in
 * the type.
 */ export var getUnknownProperty = function(indexable, key) {
    var asRecord = indexable;
    return asRecord[key];
};
/**
 * Return the given value of `indexable` at the given key, if it is a string,
 * and otherwise return `undefined`.
 */ export var getStringProperty = function(indexable, key) {
    var valueUnknown = getUnknownProperty(indexable, key);
    return typeof valueUnknown === "string" ? valueUnknown : undefined;
};
export var hasErrorMessage = function(error) {
    return "message" in error && typeof error.message === "string";
};
var hasErrorName = function(error) {
    return "name" in error && typeof error.name === "string";
};
var maybeHasErrorStack = function(error) {
    return !("stack" in error) || typeof error.stack === "string";
};
var isErrorLike = function(error) {
    return (typeof error === "undefined" ? "undefined" : _type_of(error)) === "object" && !isNullish(error) && hasErrorMessage(error) && hasErrorName(error) && maybeHasErrorStack(error);
};
export var isError = function(error) {
    return error instanceof Error || isErrorLike(error);
};
/**
 * Returns true if `maybePromise` implements the `Promise` interface (.then and .catch)
 */ export var isPromise = function(maybePromise) {
    return typeof (maybePromise === null || maybePromise === void 0 ? void 0 : maybePromise.then) === "function" && typeof (maybePromise === null || maybePromise === void 0 ? void 0 : maybePromise.catch) === "function";
};
/**
 * Return object with the first N elements
 * Note: There is no guarantee on iteration order, and may produce different results across browsers
 */ export var firstNObjectValues = function(numElements, object) {
    var entries = Object.entries(object).slice(0, numElements);
    return Object.fromEntries(entries);
};
